"use client";

import { createContext, ReactNode, useContext, useRef, useState } from 'react';
import { useChat as useAIChat } from '@ai-sdk/react';
import { DefaultChatTransport, ChatStatus, UIMessage, UIDataTypes, UITools } from 'ai';
import { Sparkles, ArrowUp, Paperclip, Square, X, Brain } from "lucide-react";
import { toast } from "sonner"
import { Article, Summary, Highlight, Form, Profile } from '@/types';
import { ChatContainerContent, ChatContainerRoot } from "@/components/ui/chat-container";
import { cn } from "@/lib/utils"
import { Button } from '@/components/ui/button';
import { Markdown } from "@/components/ui/markdown"
import { Widget } from '@/components/chat/widget';
import { Message, MessageAvatar } from '@/components/ui/message';
import { PromptInput, PromptInputAction, PromptInputActions, PromptInputTextarea } from "@/components/ui/prompt-input"

type ChatContextProps = {
    status: ChatStatus | null;
    artifact: Article | Summary | Highlight | Form | Profile | null;
    messages: UIMessage<unknown, UIDataTypes, UITools>[]
    isOpen: boolean;

    sendMessage: (message: any) => void;
    stop: () => void;
}

type Props = {
    children: ReactNode;
}

const ChatContext = createContext<ChatContextProps>({
    status: null,
    artifact: null,
    messages: [],
    isOpen: false,

    sendMessage: (message: any) => { },
    stop: () => { },
});

export const useChat = () => useContext(ChatContext);
export const ChatProvider = ({ children }: Props) => {
    const { status, messages, setMessages, sendMessage, error, stop } = useAIChat({
        transport: new DefaultChatTransport({
            api: '/ai',
            body: () => ({
                context: {
                    url: window.location.href,
                    selectedText: window.getSelection()?.toString() || "",
                }
            }),
        })
    });
    const [artifact, setArtifact] = useState<Article | Summary | Highlight | Form | Profile | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [input, setInput] = useState("")
    const [files, setFiles] = useState<File[]>([])
    const uploadInputRef = useRef<HTMLInputElement>(null)

    const handleSubmit = async () => {
        if (input.trim() === "" && files.length === 0) return;

        const messageContent: any = {
            text: input,
        };

        if (files.length > 0) {
            messageContent.files = files;
        }

        sendMessage(messageContent);
        setInput("");
        setFiles([]);
    }

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files) {
            return;
        }

        const newFiles = Array.from(event.target.files)
        const MAX_FILES = 5;
        const MAX_SIZE_MB = 2;
        const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

        if (files.length + newFiles.length > MAX_FILES) {
            toast.error(`You can only upload a maximum of ${MAX_FILES} files.`);
            return;
        }

        const validFiles = newFiles.filter(file => {
            if (file.size > MAX_SIZE_BYTES) {
                toast.error(`File ${file.name} exceeds the ${MAX_SIZE_MB}MiB size limit.`);
                return false;
            }

            return true;
        });

        setFiles((prev) => [...prev, ...validFiles])
    }

    const handleRemoveFile = (index: number) => {
        setFiles((prev) => prev.filter((_, i) => i !== index))
        if (uploadInputRef?.current) {
            uploadInputRef.current.value = ""
        }
    }

    return (
        <ChatContext.Provider value={{ status, messages, artifact, isOpen, sendMessage, stop }}>
            {children}
            <Widget isOpen={isOpen} onClose={() => setIsOpen(false)} onCreateNewSession={() => { setMessages([]); setIsOpen(false); }}>
                <div className="flex h-full flex-col overflow-hidden">
                    <ChatContainerRoot className="relative flex-1 min-h-0 flex flex-col overflow-hidden">
                        <ChatContainerContent className="flex-1 overflow-y-auto space-y-6 flex flex-col px-4 py-6">
                            {messages.length > 0 && messages.map((message) => {
                                const isAssistant = message.role === "assistant"

                                return (
                                    <Message key={message.id} className={message.role === "user" ? "justify-end" : "justify-start"}>
                                        {isAssistant && (<MessageAvatar src="/images/logo.svg" alt="Aura" />)}
                                        {message.parts.map((part, i) => {
                                            if (part.type === 'text') {
                                                return <Markdown key={i} className={cn("rounded-lg px-3 py-2 prose break-words whitespace-normal", isAssistant ? "bg-white text-black" : "bg-sky-700 text-white")}>{part.text}</Markdown>;
                                            } else if (part.type.startsWith('tool-')) {
                                                const toolName = part.type.replace('tool-', '');
                                                return <div key={i} className="rounded-lg px-3 py-2 prose break-words whitespace-normal bg-white text-black">{JSON.stringify(part)}</div>;
                                            }
                                        })}
                                    </Message>
                                )
                            })}
                        </ChatContainerContent>
                    </ChatContainerRoot>
                    <div className="mx-auto w-full shrink-0 z-10">
                        <PromptInput className="w-full" value={input} onValueChange={setInput} isLoading={status === "streaming"} onSubmit={handleSubmit}>
                            {files.length > 0 && (
                                <div className="flex flex-wrap gap-2 pb-2">
                                    {files.map((file, index) => (
                                        <div key={index} onClick={e => e.stopPropagation()}
                                            className="bg-blue-50 text-slate-900 border border-blue-300 flex items-center gap-2 rounded-md px-3 py-2 text-sm"
                                        >
                                            <Paperclip className="size-4" />
                                            <span className="max-w-[120px] truncate">{file.name}</span>
                                            <button onClick={() => handleRemoveFile(index)} className="hover:bg-sky-800 hover:text-white rounded-full p-1 cursor-pointer">
                                                <X className="size-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <PromptInputTextarea placeholder="Ask me anything..." />
                            <PromptInputActions className="flex items-center justify-between gap-2 pt-2">
                                <div className="flex items-center gap-2">
                                    <PromptInputAction tooltip="Attach files">
                                        <label htmlFor="file-upload" className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-2xl hover:bg-gray-100">
                                            <input id="file-upload" type="file" className="hidden" multiple onChange={handleFileChange} accept="image/jpeg, image/png, image/webp, text/plain, text/markdown, application/pdf, application/xml" />
                                            <Paperclip className="text-primary size-5" />
                                        </label>
                                    </PromptInputAction>
                                </div>

                                <PromptInputAction tooltip={status === 'streaming' ? "Stop generation" : "Send message"}>
                                    <Button variant="default" size="icon" className="h-9 w-9 rounded-full" onClick={handleSubmit} disabled={status === 'streaming' || (input.trim() === "")}>
                                        {status === 'streaming' ? (<Square className="size-5" />) : (<ArrowUp className="size-5" />)}
                                    </Button>
                                </PromptInputAction>
                            </PromptInputActions>
                        </PromptInput>
                    </div>
                </div>
            </Widget>
            {!isOpen && (
                <Button className="fixed bottom-6 right-6 z-50 rounded-full text-black p-0 w-14 h-14 bg-white shadow-lg hover:bg-blue-50 border-[2px] border-blue-300" onClick={() => setIsOpen(!isOpen)}>
                    <Sparkles className="m-auto" />
                </Button>
            )}
            {error && toast.error(`${error.message}`)}
        </ChatContext.Provider>
    );
};